import { useCallback, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { basketChosenAddressChange, basketPostcodeChange } from 'actions/basket'
import type { BasketAddress } from 'reducers/basket'
import { fetchData } from 'routes/Menu/fetchData'
import { getChosenAddressId } from 'selectors/basket'
import { getUserShippingAddresses } from 'selectors/user'
import type { ImmutableList } from 'utils/types/immutable'

import { formatAddressLines, formatAddressName } from './useDeliverySettings'

export type AddressOption = {
  id: string
  name: string
  lines: string[]
}

export type UseEditAddress = {
  savedAddresses: AddressOption[]
  currentAddressId: string
  selectedAddressId: string
  setSelectedAddressId: (id: string) => void
  canSave: boolean
  confirm: () => void
}

export const useEditAddress = (): UseEditAddress => {
  const dispatch = useDispatch()
  const shippingAddresses = useSelector(getUserShippingAddresses) as
    | ImmutableList<BasketAddress>
    | undefined
  const currentAddressId = useSelector(getChosenAddressId) ?? ''

  const savedAddresses: AddressOption[] = useMemo(() => {
    if (!shippingAddresses) return []

    return shippingAddresses
      .toArray()
      .map((address) => ({
        id: address.get('id'),
        name: formatAddressName(address),
        lines: formatAddressLines(address),
      }))
      .filter((option) => !!option.id)
  }, [shippingAddresses])

  const [selectedAddressId, setSelectedAddressId] = useState<string>(currentAddressId)

  const hasChanged = selectedAddressId !== currentAddressId

  const confirm = useCallback(() => {
    if (!hasChanged) return
    const selected = shippingAddresses?.find((address) => address.get('id') === selectedAddressId)
    if (!selected) return

    dispatch(basketChosenAddressChange(selected))
    // basketPostcodeChange also reloads boxSummaryDeliveryDays for the new postcode.
    const newPostcode = selected.get('postcode')
    if (newPostcode) {
      dispatch(basketPostcodeChange(newPostcode))
    }
    dispatch(
      fetchData({ query: {}, params: {} }, true, undefined, {
        addRecipe: () => {},
      }),
    )
  }, [dispatch, hasChanged, selectedAddressId, shippingAddresses])

  return {
    savedAddresses,
    currentAddressId,
    selectedAddressId,
    setSelectedAddressId,
    canSave: hasChanged,
    confirm,
  }
}
